import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { startTranslationJob, MAX_ATTEMPTS } from "../translateJob";
import { useAuth } from "../components/ProtectedRoute";

const API = ""; // same-origin now — Vercel rewrites /api/* to the backend (see client/vercel.json). Keeps the session cookie first-party for Safari/Firefox.

/* ─────────────────────────────────────────
   CHECKPOINT HELPERS — localStorage mein
   progress atomically save hoti hai.
   (Only fully completed chunks are ever written — see translateJob.js.)

   Why this was fragile: every write used to contain the WHOLE parsed subtitle ("subs") and was
   rewritten after every chunk, and checkpoints of other files were never removed. localStorage
   (~5 MB) filled up, setItem threw, the error was swallowed and the progress silently vanished
   -> Resume / Dashboard started again from 0%. Now:
     * the checkpoint holds only the translated lines (subs are re-fetched on resume anyway);
     * if storage is still full, the OLDEST other checkpoints are dropped until this one fits;
     * a copy is always kept in memory too, so Resume keeps working inside this tab even if
       storage is blocked;
     * a failed write is reported to the UI (never silent).
───────────────────────────────────────── */
const CHECKPOINT_PREFIX = "subai_checkpoint_";
const CHECKPOINT_TTL_MS = 24 * 60 * 60 * 1000;
const memCheckpoints = new Map();   // fileId -> checkpoint (lives until the page is reloaded)

function checkpointKeys() {
  try { return Object.keys(localStorage).filter(k => k.startsWith(CHECKPOINT_PREFIX)); } catch { return []; }
}

// Remove expired / unreadable checkpoints of other files.
function pruneCheckpoints(keepFileId) {
  for (const k of checkpointKeys()) {
    if (k === CHECKPOINT_PREFIX + keepFileId) continue;
    try {
      const p = JSON.parse(localStorage.getItem(k));
      if (!p || !p.savedAt || Date.now() - p.savedAt > CHECKPOINT_TTL_MS) localStorage.removeItem(k);
    } catch { try { localStorage.removeItem(k); } catch {} }
  }
}

// Returns true if the checkpoint reached localStorage, false if only the in-memory copy exists.
function saveCheckpoint(fileId, data) {
  const { subs, ...slim } = data;            // eslint-disable-line no-unused-vars
  const payload = { ...slim, savedAt: Date.now() };
  memCheckpoints.set(fileId, payload);
  const key  = CHECKPOINT_PREFIX + fileId;
  let json;
  try { json = JSON.stringify(payload); } catch { return false; }
  try { localStorage.setItem(key, json); return true; } catch { /* full or blocked -> make room below */ }

  const others = checkpointKeys().filter(k => k !== key).map(k => {
    let t = 0; try { t = JSON.parse(localStorage.getItem(k))?.savedAt || 0; } catch {}
    return [k, t];
  }).sort((a, b) => a[1] - b[1]);              // oldest first
  for (const [k] of others) {
    try { localStorage.removeItem(k); localStorage.setItem(key, json); return true; } catch { /* try the next one */ }
  }
  console.warn("[Checkpoint] localStorage unavailable/full — progress kept in memory only");
  return false;
}

function loadCheckpoint(fileId) {
  let stored = null;
  try { const raw = localStorage.getItem(CHECKPOINT_PREFIX + fileId); if (raw) stored = JSON.parse(raw); } catch {}
  const mem  = memCheckpoints.get(fileId) || null;
  const best = [stored, mem].filter(Boolean)
    .sort((a, b) => (b.translatedSoFar?.length || 0) - (a.translatedSoFar?.length || 0))[0] || null;
  if (!best) return null;
  // Expire checkpoints older than 24 hours
  if (Date.now() - (best.savedAt || 0) > CHECKPOINT_TTL_MS) { clearCheckpoint(fileId); return null; }
  return best;
}

function clearCheckpoint(fileId) {
  memCheckpoints.delete(fileId);
  try { localStorage.removeItem(CHECKPOINT_PREFIX + fileId); } catch {}
}

/* ─────────────────────────────────────────
   RUN RECORD — sessionStorage. Lets a page refresh restore the settings of the run, and
   remembers which navigation (location.key) launched it so a REFRESH / BACK never silently
   restarts a translation: it shows the resume prompt instead.
───────────────────────────────────────── */
const RUN_PREFIX = "subai_session_";

function readRun(fileId) {
  try { return JSON.parse(sessionStorage.getItem(RUN_PREFIX + fileId)); } catch { return null; }
}
function writeRun(fileId, data) {
  try { sessionStorage.setItem(RUN_PREFIX + fileId, JSON.stringify({ ...data, savedAt: Date.now() })); } catch {}
}
function clearRun(fileId) {
  try { sessionStorage.removeItem(RUN_PREFIX + fileId); } catch {}
}
function latestRun() {
  try {
    let best = null;
    for (const k of Object.keys(sessionStorage)) {
      if (!k.startsWith(RUN_PREFIX)) continue;
      const r = JSON.parse(sessionStorage.getItem(k));
      if (r?.fileId && (!best || (r.savedAt || 0) > (best.savedAt || 0))) best = r;
    }
    return best;
  } catch { return null; }
}

/* ─────────────────────────────────────────
   ERROR PRESENTATION — one entry per distinct failure type the server / job can report.
   (The server's own message is always shown too; this adds a title + next step.)
───────────────────────────────────────── */
const ERROR_INFO = {
  invalid_key:      { icon: "❌", title: "API key galat hai",              hint: "Sahi key daal ke Resume karo. Jo lines ho chuki hain wo save hain.", newKey: true },
  quota:            { icon: "📛", title: "Daily quota / credits khatam",    hint: "Doosri API key se Resume karo ya quota reset hone ka wait karo. Progress save hai.", newKey: true },
  rate_limit:       { icon: "⏱️", title: "Rate limit (RPM) hit",           hint: "Thoda wait karke Resume karo. Dashboard mein Pacing 'Safe' ya chhota batch chun sakte ho." },
  model_unavailable:{ icon: "🧩", title: "Model available nahi",           hint: "Dashboard mein doosra model chuno ya Gemini ke liye Auto mode use karo, phir Resume karo." },
  overload:         { icon: "🔄", title: "Provider busy / overloaded",      hint: "2-3 min baad Resume karo. Progress save hai." },
  network:          { icon: "📡", title: "Network / server error",          hint: "Internet check karo aur Resume karo." },
  invalid_response: { icon: "⚠️", title: "Provider ka response invalid",    hint: "Resume karne par ye chunk dobara bheja jayega." },
  safety:           { icon: "🛡️", title: "Content safety filter ne block kiya", hint: "Dusra model / provider try karo." },
  budget:           { icon: "🛑", title: "Is chunk ki retry limit poori",   hint: "Aap manually Resume kar sakte ho — ye naya attempt hoga." },
  duplicate:        { icon: "🔁", title: "Duplicate request block hui",     hint: "Ye chunk pehle se process ho raha hai (doosra tab / purana run?). Kuch seconds baad Resume karo." },
  region:           { icon: "🌍", title: "API aapki location mein enabled nahi", hint: "aistudio.google.com pe check karo.", newKey: true },
  session:          { icon: "🔒", title: "Session expire ho gaya",          hint: "Dobara login karo, phir Dashboard se Resume karo.", login: true },
  ad_required:      { icon: "💎", title: "Pehle ek ad dekho",               hint: "Dashboard pe jaake 'Ad Dekho' dabao — 1 free translation unlock hogi.", noResume: true },
  fetch_failed:     { icon: "📂", title: "File nahi mili / fetch fail",     hint: "Dashboard pe dobara upload karo.", noResume: true },
  save_failed:      { icon: "💾", title: "File save fail hui",              hint: "Saari lines translate ho chuki hain aur save hain — Resume karne par sirf save step dobara chalega." },
  cancelled:        { icon: "🚫", title: "Translation cancel ki gayi",      hint: "Poore ho chuke chunks save hain — Resume ya Start fresh." },
  model_invalid:    { icon: "🧩", title: "Model valid nahi",                hint: "Dashboard mein dobara model chuno.", noResume: true },
  bad_request:      { icon: "⚠️", title: "Request invalid",                 hint: "Dashboard se dobara try karo.", noResume: true },
  error:            { icon: "⚠️", title: "Translation fail hui",            hint: "Resume karke dobara try karo." },
};

const PHASE_LABEL = {
  fetching:        "Fetching subtitle file",
  selecting_model: "Selecting model…",
  waiting_rpm:     "Waiting for rate-limit gap",
  translating:     "Translating chunk",
  retrying:        "Waiting to retry chunk",
  saving:          "Saving translated file",
  done:            "Complete",
  error:           "Stopped (error)",
  cancelled:       "Stopped (cancelled)",
};

const INITIAL_VIEW = {
  phase: "idle", status: "Initializing...", progress: 0, totalLines: 0, doneLines: 0,
  chunkNum: 0, totalChunks: 0, attempt: null, countdown: null, retryReason: null,
  model: null, modelMode: null, requestedModel: null, switchNotice: null,
  error: null, done: false, downloadName: "", notice: null, cpWarning: null,
};

/* ─────────────────────────────────────────
   PRESENTATIONAL ONLY — small "now playing"
   style equalizer used next to the progress
   number while a translation is actively running.
   Carries no state of its own; purely decorative.
───────────────────────────────────────── */
function EqualizerBars({ active, color }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 18 }} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          style={{
            width: 3,
            borderRadius: 2,
            background: color,
            height: 7,
            animation: active ? `eqBar 1s ease-in-out ${i * 0.13}s infinite` : "none",
            opacity: active ? 1 : 0.35,
          }}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────
   PRESENTATIONAL ONLY — compact stat cell used
   in the translation-statistics grid.
───────────────────────────────────────── */
function StatCell({ label, value }) {
  return (
    <div style={S.statCell}>
      <span style={S.statLabel}>{label}</span>
      <span style={S.statValue}>{value}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={S.infoRow}>
      <span style={S.infoLabel}>{label}</span>
      <span style={S.infoValue}>{value}</span>
    </div>
  );
}

const PROVIDER_LABELS = { openrouter: "OpenRouter", llama: "Llama AI", openai: "OpenAI", agentrouter: "AgentRouter", gemini: "Gemini" };

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function ConvertPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth     = useAuth();
  const st       = location.state || {};

  // Run configuration. Comes from Dashboard (location.state) or, after a refresh, from sessionStorage.
  // Kept in a ref so the long-running job never reads a stale render's value.
  const cfgRef = useRef(null);
  const [cfg, setCfg] = useState(null);

  const [v, setV] = useState(INITIAL_VIEW);
  const patch = (p) => setV((s) => ({ ...s, ...p }));

  const [angle,         setAngle]         = useState(0);
  const [expiryWarning, setExpiryWarning] = useState(true);
  const [downloadUrl,   setDownloadUrl]   = useState("");

  // ── RESUME FEATURE STATE ──
  const [checkpoint,       setCheckpoint]       = useState(null);   // checkpoint found on entry / after a stop
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [interrupted,      setInterrupted]      = useState(false);  // refresh / back to a run that was already started
  const [resumeApiKey,     setResumeApiKey]     = useState("");
  const [showResumeKey,    setShowResumeKey]    = useState(false);
  const [showResumeKeyInError, setShowResumeKeyInError] = useState(false);
  const [errorResumeKey,   setErrorResumeKey]   = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [savedCp,          setSavedCp]          = useState(null);   // checkpoint summary shown on the error card

  // ── LIVE TRANSLATE VIEW (Ultra Pro only) ──
  // canUseLiveTranslate comes from /api/profile (shared AuthProvider). The toggle preference is
  // remembered per-browser. The job reads the toggle through refs, so it is never stale.
  const canUseLiveTranslate = !!auth?.user?.canUseLiveTranslate;
  const [liveTranslateOn, setLiveTranslateOn] = useState(() => {
    try { return localStorage.getItem("subai_live_translate") === "1"; } catch { return false; }
  });
  const [liveLines, setLiveLines] = useState([]); // [{ original, translated }, ...] — grows as chunks complete
  const liveTranslateOnRef     = useRef(liveTranslateOn);
  const canUseLiveTranslateRef = useRef(canUseLiveTranslate);
  useEffect(() => { liveTranslateOnRef.current = liveTranslateOn; }, [liveTranslateOn]);
  useEffect(() => { canUseLiveTranslateRef.current = canUseLiveTranslate; }, [canUseLiveTranslate]);

  const toggleLiveTranslate = () => {
    setLiveTranslateOn(prev => {
      const next = !prev;
      try { localStorage.setItem("subai_live_translate", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const angleRef  = useRef(0);
  const animRef   = useRef(null);
  const jobRef    = useRef(null);     // the ONE active job (or null)
  const launchRef = useRef(null);     // token of the latest launch — callbacks from older launches are ignored
  let token = null;
  try { token = localStorage.getItem("subai_session"); } catch {}   // non-sensitive "am I logged in" flag — real session is the httpOnly cookie

  // Spin animation (always running)
  useEffect(() => {
    const spin = () => {
      angleRef.current = (angleRef.current + 1.2) % 360;
      setAngle(angleRef.current);
      animRef.current = requestAnimationFrame(spin);
    };
    animRef.current = requestAnimationFrame(spin);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  /* ──────────────────────────────────────
     JOB LIFECYCLE
     * start is deferred by one timer tick, so React StrictMode's mount → cleanup → mount
       replay cancels the first (never-started) attempt and starts exactly ONE job.
     * leaving the page for any reason (route change, Back, refresh, tab close) cancels the job:
       its fetch is aborted, its timers are cleared and the server is told to stop.
       Completed chunks stay in the checkpoint, so nothing is lost.
  ────────────────────────────────────── */
  useEffect(() => {
    const timer = setTimeout(init, 0);
    const onPageHide = () => { jobRef.current?.cancel("pagehide"); };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pagehide", onPageHide);
      launchRef.current = null;
      jobRef.current?.cancel("unmount");
      jobRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // bfcache restore: the page comes back with a job we cancelled on pagehide -> show that honestly.
  useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted || !jobRef.current?.isCancelled()) return;
      stopWithCancelState("Page background mein gaya tha, isliye translation roki gayi.", false);
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function init() {
    let c = null;
    if (st.fileId && st.apiKey) {
      c = {
        fileId: st.fileId, apiKey: st.apiKey, provider: st.provider || "gemini", orModel: st.orModel || null,
        geminiModel: st.geminiModel || null, modelSelectionMode: st.modelSelectionMode || "auto",
        batchSize: st.batchSize, maxBatch: st.maxBatch, quality: st.quality || "balanced", pacing: st.pacing || "safe",
        sourceLang: st.sourceLang || null, fileName: st.fileName || "",
      };
    } else {
      const saved = latestRun();   // page refreshed and navigation state was lost
      if (saved?.apiKey) c = { ...saved };
    }
    if (!c || !c.fileId || !c.apiKey) { navigate("/dashboard", { replace: true }); return; }

    pruneCheckpoints(c.fileId);
    const previous = readRun(c.fileId);
    const wasInterrupted = !!(previous && previous.activeKey && previous.activeKey === location.key);
    cfgRef.current = c;
    setCfg(c);
    writeRun(c.fileId, { ...c, activeKey: previous?.activeKey || null });

    const existing = loadCheckpoint(c.fileId);
    const hasCp = !!(existing && existing.translatedSoFar && existing.translatedSoFar.length > 0);
    if (hasCp || wasInterrupted) {
      // Never auto-start over saved progress, and never auto-restart a run that was already started
      // (refresh / Back): the user decides.
      setCheckpoint(hasCp ? existing : null);
      setInterrupted(wasInterrupted);
      setShowResumePrompt(true);
      patch({ totalLines: existing?.totalLines || 0, doneLines: existing?.translatedSoFar?.length || 0 });
    } else {
      launch(null, c.apiKey);
    }
  }

  const readCpSummary = (fileId) => {
    const cp = loadCheckpoint(fileId);
    return cp && cp.translatedSoFar?.length ? { done: cp.translatedSoFar.length, total: cp.totalLines || 0 } : null;
  };

  /* One run == one job. Every Resume / Retry / Start-fresh creates a NEW job (new id). */
  function launch(cp, apiKey) {
    const c = cfgRef.current;
    if (!c) return;
    jobRef.current?.cancel("replaced");        // defensive: there is never more than one live job
    const launchToken = {};
    launchRef.current = launchToken;

    const isGemini = c.provider === "gemini";
    writeRun(c.fileId, { ...c, apiKey, activeKey: location.key });
    setCfg((prev) => ({ ...(prev || c), apiKey }));
    cfgRef.current = { ...c, apiKey };

    setShowResumePrompt(false);
    setShowResumeKeyInError(false);
    setErrorResumeKey("");
    setSavedCp(null);
    setDownloadUrl("");
    setLiveLines([]);
    setV({
      ...INITIAL_VIEW, phase: "fetching",
      progress: cp?.translatedSoFar?.length ? 5 + Math.round((cp.translatedSoFar.length / (cp.totalLines || 1)) * 88) : 2,
      status: "File fetch ho rahi hai...",
      totalLines: cp?.totalLines || 0, doneLines: cp?.translatedSoFar?.length || 0,
      modelMode: isGemini ? c.modelSelectionMode : null,
    });

    const alive = () => launchRef.current === launchToken;
    const job = startTranslationJob({
      fileId: c.fileId, apiKey, provider: c.provider,
      model: isGemini ? c.geminiModel : c.orModel,
      modelSelectionMode: c.modelSelectionMode,
      batchSize: c.batchSize, maxBatch: c.maxBatch,
      quality: c.quality, pacing: c.pacing, sourceLang: c.sourceLang, fileName: c.fileName,
      checkpoint: cp,
    }, {
      emit: (p) => { if (alive()) patch(p); },
      saveCheckpoint: (data) => saveCheckpoint(c.fileId, data),   // true = in localStorage, false = memory only
      clearCheckpoint: () => { clearCheckpoint(c.fileId); },
      liveEnabled: () => liveTranslateOnRef.current && canUseLiveTranslateRef.current,
      onChunkDone: (info) => {
        if (!alive()) return;
        if (info.reset) setLiveLines(info.pairs);
        else setLiveLines((prev) => [...prev, ...info.pairs]);
      },
    });
    jobRef.current = job;

    job.promise.then((res) => {
      if (!alive() || jobRef.current !== job) return;       // a newer run / cancel owns the UI now
      if (res.status === "done") {
        clearRun(c.fileId);
        const url = `${API}/api/download/${encodeURIComponent(c.fileId)}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
        setDownloadUrl(url);
        patch({ phase: "done", done: true, progress: 100, status: "Done!", countdown: null, downloadName: res.downloadName || "" });
      } else if (res.status === "error") {
        const info = ERROR_INFO[res.error.type] || ERROR_INFO.error;
        setSavedCp(readCpSummary(c.fileId));
        setShowResumeKeyInError(!!info.newKey);
        patch({ phase: "error", error: res.error, countdown: null, model: res.error.model || undefined });
      }
      // "cancelled" is handled synchronously by the Cancel handler (stopWithCancelState)
    });
  }

  /* Shows the cancelled state. Never claims the provider stopped unless that is known. */
  function stopWithCancelState(extraNote, wasInFlight) {
    const c = cfgRef.current;
    const providerLabel = PROVIDER_LABELS[c?.provider] || "Provider";
    const note = wasInFlight
      ? `\n\n${providerLabel} ko jaa chuki request upstream pe poori ho sakti hai (quota use ho sakta hai) — uska result ignore kar diya gaya hai.`
      : "";
    if (c) setSavedCp(readCpSummary(c.fileId));
    patch({
      phase: "cancelled", countdown: null, done: false,
      error: { type: "cancelled", message: (extraNote || "Translation cancel kar di gayi.") + note },
    });
  }

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    const r = jobRef.current?.cancel("user");
    if (!r) return;
    if (r.blocked === "saving") { patch({ notice: "File save ho rahi hai — kuch seconds mein complete hoga, tab tak cancel nahi ho sakta." }); return; }
    if (r.cancelled) stopWithCancelState("Translation cancel kar di gayi.", r.wasInFlight);
  };

  /* ──────────────────────────────────────
     RESUME: User ne "Resume" choose kiya
  ────────────────────────────────────── */
  const handleResume = () => {
    const c = cfgRef.current;
    const keyToUse = resumeApiKey.trim() || c.apiKey;
    launch(checkpoint, keyToUse);
  };

  /* FRESH START: Discard checkpoint (explicit user action only) */
  const handleFreshStart = () => {
    const c = cfgRef.current;
    clearCheckpoint(c.fileId);
    setCheckpoint(null);
    launch(null, resumeApiKey.trim() || c.apiKey);
  };

  /* After an error / cancel: resume from the saved checkpoint with the same key */
  const doResume = (newKey) => {
    const c = cfgRef.current;
    const keyToUse = (newKey || "").trim() || c.apiKey;
    launch(loadCheckpoint(c.fileId), keyToUse);
  };

  const doStartFresh = () => {
    const c = cfgRef.current;
    clearCheckpoint(c.fileId);
    launch(null, c.apiKey);
  };

  /* ─── Render-only derived values ─── */
  const isWaiting    = v.phase === "waiting_rpm" || v.phase === "retrying";
  const isCancelled  = v.phase === "cancelled";
  const isError      = v.phase === "error";
  const stopped      = isError || isCancelled;
  const done         = v.done;
  const errInfo      = v.error ? (ERROR_INFO[v.error.type] || ERROR_INFO.error) : null;
  const accent       = done ? "#34D399" : isCancelled ? "#E8B34D" : isError ? "#F45B69" : isWaiting ? "#F5A623" : "#7C5CFF";
  const accentSoft   = done ? "rgba(52,211,153,0.14)" : isCancelled ? "rgba(232,179,77,0.14)" : isError ? "rgba(244,91,105,0.14)" : isWaiting ? "rgba(245,166,35,0.14)" : "rgba(124,92,255,0.14)";
  const statusLabel  = done ? "Done" : isCancelled ? "Cancelled" : isError ? "Error" : v.phase === "retrying" ? "Retrying" : "Translating";
  const provider     = cfg?.provider || st.provider || "gemini";
  const providerLabel = PROVIDER_LABELS[provider] || "Gemini";
  const fileName     = cfg?.fileName || st.fileName || "";
  const sourceLang   = cfg?.sourceLang || st.sourceLang;
  const sourceLangLabel = { english: "English", japanese: "Japanese", spanish: "Spanish", russian: "Russian", german: "German" }[sourceLang] || "Auto-detect";
  const maxBatch     = cfg?.maxBatch || 80;
  const chunkSizeDisplay = Math.min(Math.max(Number(cfg?.batchSize || st.batchSize) || 20, 1), maxBatch);
  const totalChunksDisplay = v.totalChunks || (v.totalLines ? Math.ceil(v.totalLines / chunkSizeDisplay) : 0);
  const currentChunkDisplay = v.chunkNum || (v.totalLines ? Math.min(totalChunksDisplay, Math.floor(v.doneLines / chunkSizeDisplay) + 1) : 0);

  const modeLabel = provider === "gemini"
    ? ((v.modelMode || cfg?.modelSelectionMode) === "manual" ? "Manual" : "Auto")
    : "Provider model";
  // The model shown is the one the SERVER reported. Before the first answer we only know what was requested.
  const requested = provider === "gemini"
    ? (cfg?.modelSelectionMode === "manual" ? cfg?.geminiModel : null)
    : (cfg?.orModel || null);
  const modelDisplay = v.model || (requested ? `${requested} (requested)` : "Selecting model…");
  const operation = v.phase === "waiting_rpm" ? `Waiting rate-limit gap${v.countdown ? ` (${v.countdown}s)` : ""}`
    : v.phase === "retrying" ? `Waiting to retry${v.countdown ? ` (${v.countdown}s)` : ""}`
    : v.phase === "translating" ? `Translating chunk ${currentChunkDisplay}/${totalChunksDisplay || "?"}`
    : (PHASE_LABEL[v.phase] || "—");

  /* ──────────────────────────────────────
     RESUME PROMPT UI (checkpoint found / interrupted run) — same handlers
  ────────────────────────────────────── */
  if (showResumePrompt) {
    const cpDone  = checkpoint?.translatedSoFar?.length || 0;
    const savedPct = checkpoint ? Math.round((cpDone / (checkpoint.totalLines || 1)) * 100) : 0;
    return (
      <div style={S.page}>
        <div style={S.shell}>
          <header style={S.header}>
            <button onClick={() => navigate("/dashboard")} style={S.navLink}>← Dashboard</button>
            <span style={S.navLink}>{checkpoint ? "💾 Saved session found" : "⏸ Run paused"}</span>
          </header>

          <section style={{ ...S.metaCard, textAlign: "center" }}>
            <p style={S.fileNameText}>{checkpoint?.fileName || fileName}</p>
            <p style={S.fileSub}>
              {checkpoint ? `${cpDone} / ${checkpoint.totalLines} lines already translated` : "Koi saved progress nahi"}
            </p>
          </section>

          {interrupted && (
            <p style={S.statusLine}>
              Pichla run beech mein ruk gaya tha (refresh / wapas aana). Auto-restart nahi kiya gaya — neeche se chuno.
            </p>
          )}

          {checkpoint && (
            <section style={S.progressSection}>
              <div style={S.progressTopRow}>
                <span style={{ ...S.progressBigNum, color: "#E8B34D" }}>{savedPct}%</span>
              </div>
              <div style={S.trackOuter}>
                <div style={{ ...S.trackFill, width: `${savedPct}%`, background: "#E8B34D" }} />
              </div>
              <p style={S.progressLabel}>Pick up where you left off, or start over</p>
            </section>
          )}

          <section style={S.metaCard}>
            <label style={S.statLabel}>New API key (optional)</label>
            <div style={{ position: "relative", marginTop: 8 }}>
              <input
                type={showResumeKey ? "text" : "password"}
                value={resumeApiKey}
                onChange={e => setResumeApiKey(e.target.value)}
                placeholder="Leave blank to reuse the same key"
                autoComplete="off"
                style={{ ...S.keyInput, width: "100%" }}
              />
              <span onClick={() => setShowResumeKey(x => !x)} style={S.eyeToggle}>{showResumeKey ? "🙈" : "👁"}</span>
            </div>
          </section>

          <footer style={S.actions}>
            {checkpoint ? (
              <>
                <button onClick={handleResume} style={{ ...S.btnPrimary, flex: 1 }}>▶ Resume from {savedPct}%</button>
                <button onClick={handleFreshStart} style={{ ...S.btnDangerGhost, flex: 1 }}>Start fresh</button>
              </>
            ) : (
              <button onClick={handleFreshStart} style={{ ...S.btnPrimary, flex: 1 }}>▶ Start translation</button>
            )}
          </footer>
          {checkpoint && <p style={S.footNote}>Starting fresh deletes the saved progress.</p>}
        </div>
      </div>
    );
  }

  /* ──────────────────────────────────────
     MAIN TRANSLATE UI — header (nav + status pill) → file/language card →
     linear progress → stats grid → live model status → status line →
     pacing/retry banner → live preview → error card → actions
  ────────────────────────────────────── */
  return (
    <div style={S.page}>
      {!done && !stopped && (
        <div style={S.topNotice}>Keep this tab open — translation is running</div>
      )}

      <div style={S.shell}>
        {/* Header: nav + status */}
        <header style={S.header}>
          <button onClick={() => navigate("/dashboard")} style={S.navLink}>← Dashboard</button>
          <div style={{ ...S.statusPill, background: accentSoft, color: accent }}>
            <span
              style={{
                ...S.pulseDot,
                background: accent,
                transform: !done && !stopped
                  ? `scale(${1 + 0.35 * Math.abs(Math.sin((angle * Math.PI) / 180))})`
                  : "scale(1)",
              }}
            />
            {statusLabel}
          </div>
        </header>

        {/* File + language info */}
        <section style={S.metaCard}>
          <div style={S.fileRow}>
            <div style={S.fileIconBadge}>📄</div>
            <div style={{ minWidth: 0 }}>
              <p style={S.fileNameText}>{fileName || "Subtitle file"}</p>
              <p style={S.fileSub}>{v.totalLines > 0 ? `${v.totalLines} lines` : "Preparing…"}</p>
            </div>
          </div>
          <div style={S.divider} />
          <div style={S.langFlow}>
            <span style={S.langChip}>{sourceLangLabel}</span>
            <span style={S.langArrow}>→</span>
            <span style={S.langChipTarget}>Hinglish</span>
          </div>
        </section>

        {/* Progress — linear, not circular */}
        {!done && (
          <section style={S.progressSection}>
            <div style={S.progressTopRow}>
              <span style={{ ...S.progressBigNum, color: accent }}>{v.progress}%</span>
              {!stopped && <EqualizerBars active={!isWaiting} color={accent} />}
            </div>
            <div style={S.trackOuter}>
              <div style={{ ...S.trackFill, width: `${v.progress}%`, background: accent }} />
            </div>
            {!stopped && (
              <p style={S.progressLabel}>
                {totalChunksDisplay > 0
                  ? `Chunk ${Math.min(currentChunkDisplay, totalChunksDisplay)} of ${totalChunksDisplay}`
                  : "Getting started…"}
              </p>
            )}
          </section>
        )}

        {/* Translation statistics */}
        {!stopped && (
          <section style={S.statsGrid}>
            <StatCell label="Lines" value={`${v.doneLines}/${v.totalLines || "—"}`} />
            <StatCell label="Provider" value={providerLabel} />
            <StatCell label="Batch size" value={chunkSizeDisplay} />
          </section>
        )}

        {/* Live model status — everything here comes from the server's answers; nothing is guessed */}
        {!done && !stopped && (
          <section style={S.metaCard} aria-live="polite">
            <InfoRow label="Provider" value={providerLabel} />
            <InfoRow label="Mode" value={modeLabel} />
            <InfoRow label="Model" value={modelDisplay} />
            <InfoRow label="Chunk" value={totalChunksDisplay > 0 ? `${Math.min(currentChunkDisplay, totalChunksDisplay)} / ${totalChunksDisplay}` : "—"} />
            <InfoRow label="Lines done" value={`${v.doneLines} / ${v.totalLines || "—"}`} />
            <InfoRow label="Operation" value={operation} />
            {v.attempt ? <InfoRow label="Attempt" value={`${v.attempt} / ${MAX_ATTEMPTS}`} /> : null}
            {v.switchNotice && (
              <div style={S.switchNotice}>🔀 Model switched: {v.switchNotice}</div>
            )}
          </section>
        )}

        {/* A model switch stays visible after completion too (it tells the user which model produced the file) */}
        {done && v.model && (
          <p style={S.statusLine}>
            Model: {v.model}{v.switchNotice ? ` — 🔀 ${v.switchNotice}` : ""}
          </p>
        )}

        {v.cpWarning && !stopped && !done && (
          <p style={{ ...S.statusLine, color: "#F5A623" }}>⚠️ Browser storage full — progress sirf is tab ki memory mein save ho rahi hai. Tab refresh mat karo.</p>
        )}

        {/* Current processing status */}
        {!stopped && (
          <p style={S.statusLine}>{done ? "Translation complete." : (v.notice || v.status)}</p>
        )}

        {/* Retry / pacing banner */}
        {isWaiting && !done && !stopped && (
          <div style={S.retryBanner}>
            <span style={{ fontSize: 16 }}>{v.phase === "retrying" ? "🔄" : "⏱️"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.retryTitle}>
                {v.phase === "retrying"
                  ? `${providerLabel} — retry ${v.attempt || 2}/${MAX_ATTEMPTS}${v.retryReason ? ` (${v.retryReason})` : ""}`
                  : "Pacing next request (rate-limit safe gap)"}
              </div>
              <div style={S.retrySub}>
                Chunk {currentChunkDisplay}{totalChunksDisplay ? ` / ${totalChunksDisplay}` : ""}
              </div>
            </div>
            {v.countdown !== null && v.countdown > 0 && <div style={S.retryCd}>{v.countdown}s</div>}
          </div>
        )}

        {/* Live translate preview */}
        {canUseLiveTranslate && !done && !stopped && (
          <section style={S.metaCard}>
            <div style={S.liveToggleRow} onClick={toggleLiveTranslate}>
              <span>👁 Live preview</span>
              <div style={{ ...S.toggleTrack, background: liveTranslateOn ? "#22D3C5" : "#26262E" }}>
                <div style={{ ...S.toggleThumb, left: liveTranslateOn ? 20 : 2 }} />
              </div>
            </div>
            {liveTranslateOn && liveLines.length > 0 && (
              <div style={S.livePanelBody}>
                {liveLines.slice(-6).map((pair, i) => (
                  <div key={liveLines.length - 6 + i} style={S.liveRow}>
                    <p style={S.liveOriginal}>{pair.original}</p>
                    <p style={S.liveTranslated}>{pair.translated}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Expiry warning */}
        {done && expiryWarning && (
          <div style={S.expiryBanner}>
            <span style={{ fontSize: 16 }}>⏳</span>
            <div style={{ flex: 1 }}>
              <div style={S.expiryTitle}>File deletes in 10 days</div>
              <div style={S.expirySub}>Download it now.</div>
            </div>
            <span onClick={() => setExpiryWarning(false)} style={S.expiryClose}>✕</span>
          </div>
        )}

        {/* Error / cancelled card — never shows "completed"; never navigates away by itself */}
        {stopped && v.error && errInfo && (
          <section style={isCancelled ? { ...S.errorCard, background: "#1D1A12", border: "1px solid #E8B34D33" } : S.errorCard}>
            <p style={{ ...S.errorTitle, color: isCancelled ? "#E8B34D" : "#FF8A93" }}>{errInfo.icon} {errInfo.title}</p>
            <p style={{ ...S.errorMessage, color: isCancelled ? "#D9C79A" : "#FF8A93" }}>{v.error.message}</p>
            {v.error.model && !isCancelled && <p style={S.errorMeta}>Model: {v.error.model}</p>}
            {v.error.detail && !isCancelled && <p style={S.errorMeta}>{v.error.detail}</p>}
            <p style={S.errorHint}>{errInfo.hint}</p>

            {!savedCp && v.doneLines > 0 && (
              <p style={{ ...S.errorMeta, color: "#F5A623" }}>⚠️ Progress save nahi ho payi (browser storage full / blocked). Resume nahi ho sakega — Start fresh karna padega.</p>
            )}
            {savedCp && v.cpWarning && (
              <p style={{ ...S.errorMeta, color: "#F5A623" }}>⚠️ Browser storage full/blocked hai: progress sirf is tab ki memory mein hai. Tab refresh / band mat karo — Resume yahin se chalega.</p>
            )}

            {savedCp && (
              <div style={S.errorSaved}>
                <div style={S.progressTopRowSmall}>
                  <span style={S.statLabel}>Saved progress</span>
                  <span style={{ ...S.statValue, color: "#E8B34D" }}>{Math.round((savedCp.done / (savedCp.total || 1)) * 100)}%</span>
                </div>
                <div style={S.trackOuterSm}>
                  <div style={{ ...S.trackFillSm, width: `${Math.round((savedCp.done / (savedCp.total || 1)) * 100)}%`, background: "#E8B34D" }} />
                </div>
                <p style={S.footNote}>{savedCp.done}/{savedCp.total} lines saved — only completed chunks are stored</p>
              </div>
            )}

            {showResumeKeyInError && !errInfo.noResume && !errInfo.login && (
              <div style={{ marginTop: 10 }}>
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <input
                    type={showResumeKey ? "text" : "password"}
                    value={errorResumeKey}
                    onChange={e => setErrorResumeKey(e.target.value)}
                    placeholder="Paste a new API key…"
                    autoComplete="off"
                    style={{ ...S.keyInput, width: "100%" }}
                    autoFocus
                  />
                  <span onClick={() => setShowResumeKey(x => !x)} style={S.eyeToggle}>{showResumeKey ? "🙈" : "👁"}</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => doResume(errorResumeKey)} disabled={!errorResumeKey.trim()} style={{ ...S.btnPrimary, flex: 1, opacity: errorResumeKey.trim() ? 1 : 0.5 }}>
                    ▶ {savedCp ? "Resume" : "Start"}
                  </button>
                  <button onClick={() => setShowResumeKeyInError(false)} style={{ ...S.btnGhost, flex: 1 }}>Hide</button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Actions */}
        <footer style={S.actions}>
          {done && downloadUrl && (
            <a href={downloadUrl} download={v.downloadName || true} style={S.btnPrimary}>⬇ Download</a>
          )}
          {stopped && errInfo?.login && (
            <button onClick={() => navigate("/login")} style={S.btnPrimary}>🔒 Login</button>
          )}
          {stopped && !errInfo?.noResume && !errInfo?.login && !(errInfo?.newKey && !isCancelled) && (
            <button onClick={() => doResume("")} style={S.btnPrimary}>
              {savedCp ? "▶ Resume" : "🔄 Try again"}
            </button>
          )}
          {stopped && savedCp && !errInfo?.login && (
            <button onClick={doStartFresh} style={S.btnDangerGhostSm}>Start fresh</button>
          )}
          <button onClick={() => navigate("/dashboard")} style={S.btnGhost}>Dashboard</button>
          {!done && !stopped && (
            <button onClick={() => setShowCancelConfirm(true)} style={S.btnDangerGhostSm}>✕ Cancel</button>
          )}
        </footer>

        {stopped && !showResumeKeyInError && !errInfo?.noResume && !errInfo?.login && (
          <button onClick={() => setShowResumeKeyInError(true)} style={S.btnGold}>
            🔑 {savedCp ? "Resume with new API key" : "Start with a new API key"}
          </button>
        )}
      </div>

      {/* Cancel confirm dialog */}
      {showCancelConfirm && (
        <div style={S.dialogOverlay}>
          <div style={S.dialogCard}>
            <p style={S.dialogTitle}>Cancel this translation?</p>
            <p style={S.dialogBody}>Poore ho chuke chunks save rehte hain — baad mein resume kar sakte ho. Jo request abhi provider ke paas hai uska result ignore ho jayega.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleConfirmCancel} style={{ ...S.btnDanger, flex: 1 }}>
                Yes, cancel
              </button>
              <button onClick={() => setShowCancelConfirm(false)} style={{ ...S.btnGhost, flex: 1 }}>
                Keep going
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes eqBar { 0%,100% { height:7px; } 50% { height:17px; } }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────
   PRESENTATION LAYER — every value here is a
   style token or layout rule. No app logic lives
   below this line.
───────────────────────────────────────── */
const S = {
  page: {
    minHeight: "100vh", background: "#0B0B10", fontFamily: "var(--font-ui)",
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "16px 16px 40px",
  },
  shell: { width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: 16, marginTop: 8 },

  topNotice: {
    position: "sticky", top: 0, width: "100%", maxWidth: 420,
    background: "#18181F", border: "1px solid #26262E", borderRadius: 10,
    padding: "8px 14px", fontSize: 11.5, color: "#A8A8B3", textAlign: "center",
    marginBottom: 4,
  },

  header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  navLink: { background: "none", border: "none", color: "#A8A8B3", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "var(--font-ui)" },
  statusPill: { display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, letterSpacing: 0.3, padding: "6px 12px", borderRadius: 9999 },
  pulseDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },

  metaCard: { background: "#131318", border: "1px solid #26262E", borderRadius: 16, padding: "16px 18px" },
  fileRow: { display: "flex", alignItems: "center", gap: 12 },
  fileIconBadge: { width: 38, height: 38, borderRadius: 10, background: "#1E1E26", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 },
  fileNameText: { color: "#F5F5F7", fontSize: 14, fontWeight: 600, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fileSub: { color: "#6E6E78", fontSize: 12, margin: "2px 0 0" },
  divider: { height: 1, background: "#26262E", margin: "14px 0" },
  langFlow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10 },
  langChip: { background: "#1E1E26", color: "#A8A8B3", fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 9999 },
  langChipTarget: { background: "rgba(124,92,255,0.16)", color: "#B3A1FF", fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 9999 },
  langArrow: { color: "#4A4A52", fontSize: 13 },

  progressSection: { display: "flex", flexDirection: "column", gap: 10 },
  progressTopRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between" },
  progressTopRowSmall: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 },
  progressBigNum: { fontSize: 40, fontWeight: 800, lineHeight: 1, letterSpacing: -0.5, fontVariantNumeric: "tabular-nums" },
  progressLabel: { color: "#6E6E78", fontSize: 12, margin: 0, textAlign: "center" },
  trackOuter: { width: "100%", height: 10, background: "#1E1E26", borderRadius: 9999, overflow: "hidden" },
  trackFill: { height: "100%", borderRadius: 9999, transition: "width 0.5s ease" },
  trackOuterSm: { width: "100%", height: 6, background: "#1E1E26", borderRadius: 9999, overflow: "hidden" },
  trackFillSm: { height: "100%", borderRadius: 9999, transition: "width 0.5s ease" },

  statsGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 },
  statCell: { background: "#131318", border: "1px solid #26262E", borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 3 },
  statLabel: { color: "#6E6E78", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 },
  statValue: { color: "#F5F5F7", fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

  statusLine: { color: "#A8A8B3", fontSize: 12.5, textAlign: "center", margin: 0, lineHeight: 1.5 },

  retryBanner: { display: "flex", alignItems: "center", gap: 10, background: "rgba(245,166,35,0.08)", border: "1px solid #F5A62344", borderRadius: 12, padding: "10px 14px" },
  retryTitle: { color: "#FFC966", fontSize: 12, fontWeight: 700 },
  retrySub: { color: "#F5A62388", fontSize: 11, marginTop: 2 },
  retryCd: { color: "#FFC966", fontSize: 20, fontWeight: 800, minWidth: 38, textAlign: "right", fontVariantNumeric: "tabular-nums" },

  liveToggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", color: "#22D3C5", fontSize: 13, fontWeight: 600 },
  toggleTrack: { width: 38, height: 20, borderRadius: 10, position: "relative", transition: "background 0.2s", flexShrink: 0 },
  toggleThumb: { position: "absolute", top: 2, width: 16, height: 16, borderRadius: "50%", background: "#F5F5F7", transition: "left 0.2s" },
  livePanelBody: { marginTop: 12, paddingTop: 12, borderTop: "1px solid #26262E", maxHeight: 190, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 },
  liveRow: { borderBottom: "1px solid #1E1E26", paddingBottom: 8 },
  liveOriginal: { color: "#6E6E78", fontSize: 11.5, margin: "0 0 3px", lineHeight: 1.45, whiteSpace: "pre-line" },
  liveTranslated: { color: "#F5F5F7", fontSize: 13, margin: 0, lineHeight: 1.45, fontWeight: 500, whiteSpace: "pre-line" },

  expiryBanner: { display: "flex", alignItems: "flex-start", gap: 10, background: "rgba(232,179,77,0.08)", border: "1px solid #E8B34D44", borderRadius: 12, padding: "12px 14px" },
  expiryTitle: { color: "#E8B34D", fontWeight: 700, fontSize: 12.5 },
  expirySub: { color: "#A8A8B3", fontSize: 11.5, marginTop: 2 },
  expiryClose: { color: "#57575F", cursor: "pointer", fontSize: 15, padding: "0 2px" },

  errorCard: { background: "#1F1418", border: "1px solid #F45B6933", borderRadius: 14, padding: "16px 18px" },
  errorMessage: { color: "#FF8A93", margin: "0 0 12px", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-line" },
  errorSaved: { background: "#18181F", borderRadius: 10, padding: "10px 12px", marginBottom: 4 },

  errorTitle: { fontSize: 14, fontWeight: 800, margin: "0 0 8px" },
  errorMeta: { color: "#A8A8B3", margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.5, wordBreak: "break-word" },
  errorHint: { color: "#86868F", margin: "0 0 12px", fontSize: 12, lineHeight: 1.55 },

  infoRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "5px 0", borderBottom: "1px solid #1E1E26" },
  infoLabel: { color: "#6E6E78", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, flexShrink: 0 },
  infoValue: { color: "#F5F5F7", fontSize: 12.5, fontWeight: 600, textAlign: "right", wordBreak: "break-word" },
  switchNotice: { marginTop: 10, background: "rgba(124,92,255,0.10)", border: "1px solid #7C5CFF44", borderRadius: 10, padding: "8px 10px", color: "#B3A1FF", fontSize: 11.5, lineHeight: 1.5 },

  keyInput: { background: "#1E1E26", border: "1px solid #38383F", borderRadius: 9999, padding: "11px 40px 11px 16px", color: "#F5F5F7", fontSize: 13, fontFamily: "var(--font-ui)", outline: "none", boxSizing: "border-box" },
  eyeToggle: { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", cursor: "pointer", fontSize: 15 },

  actions: { display: "flex", flexWrap: "wrap", gap: 10 },
  btnPrimary: { flex: 1, minWidth: 120, textAlign: "center", padding: "13px", background: "#7C5CFF", border: "none", borderRadius: 9999, color: "#F5F5F7", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.6, cursor: "pointer", textDecoration: "none", fontFamily: "var(--font-ui)" },
  btnGhost: { flex: 1, minWidth: 100, textAlign: "center", padding: "13px", background: "transparent", border: "1px solid #38383F", borderRadius: 9999, color: "#A8A8B3", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-ui)" },
  btnWarning: { flex: 1, minWidth: 100, padding: "13px", background: "#F5A623", border: "none", borderRadius: 9999, color: "#0B0B10", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.6, cursor: "pointer", fontFamily: "var(--font-ui)" },
  btnGold: { width: "100%", padding: "13px", background: "#E8B34D", border: "none", borderRadius: 9999, color: "#0B0B10", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-ui)" },
  btnDangerGhost: { padding: "13px", background: "transparent", border: "1px solid #F45B6944", borderRadius: 9999, color: "#FF8A93", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-ui)" },
  btnDangerGhostSm: { padding: "13px 18px", background: "transparent", border: "1px solid #F45B6944", borderRadius: 9999, color: "#FF8A93", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-ui)" },
  btnDanger: { padding: "12px", background: "#F45B69", border: "none", borderRadius: 9999, color: "#0B0B10", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-ui)" },

  footNote: { color: "#57575F", fontSize: 10.5, textAlign: "center", margin: 0 },

  dialogOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24 },
  dialogCard: { background: "#131318", border: "1px solid #F45B6955", borderRadius: 20, padding: 24, width: "100%", maxWidth: 300, fontFamily: "var(--font-ui)", boxShadow: "0 24px 48px rgba(0,0,0,0.55)" },
  dialogTitle: { color: "#F45B69", fontWeight: 800, fontSize: 15, margin: "0 0 8px" },
  dialogBody: { color: "#86868F", fontSize: 12, margin: "0 0 20px", lineHeight: 1.6 },
};
